import {
  useInstrumentStore,
  type FxOrEnvelopeParam,
} from "../../state/instrumentStore";
import { getAudioEngine } from "../../audio/AudioEngine";
import type { TrackId } from "../../constants/scale";
import { baseGlyph, baseLabel } from "../../constants/layout";
import { Dial } from "./Dial";
import "./FxDialsPanel.css";

interface FxDialsPanelProps {
  track: TrackId;
}

const PARAMS: { key: FxOrEnvelopeParam; label: string; emoji: string }[] = [
  { key: "volume", label: "Volume", emoji: "🔊" },
  { key: "fuzz", label: "Fuzz", emoji: "⚡" },
  { key: "delay", label: "Delay", emoji: "🔁" },
  { key: "reverb", label: "Reverb", emoji: "🌊" },
  { key: "decay", label: "Decay", emoji: "⏳" },
];

export function FxDialsPanel({ track }: FxDialsPanelProps) {
  const fx = useInstrumentStore((s) => s.fx[track]);
  const subdivisions = useInstrumentStore((s) => s.subdivisions[track]);
  const setFx = useInstrumentStore((s) => s.setFx);
  const cycleSubdivisionBase = useInstrumentStore(
    (s) => s.cycleSubdivisionBase,
  );
  const toggleSubdivisionTriplet = useInstrumentStore(
    (s) => s.toggleSubdivisionTriplet,
  );
  const toggleFxPanel = useInstrumentStore((s) => s.toggleFxPanel);

  const handleChange = (param: FxOrEnvelopeParam, value: number) => {
    setFx(track, param, value);
    getAudioEngine().setFx(track, param, value);
  };

  const baseDisplay = `${baseGlyph(subdivisions.base)} ${baseLabel(subdivisions.base)}`;

  return (
    <div className="fx-dials-backdrop" onClick={() => toggleFxPanel(track)}>
      <aside
        className="fx-dials-panel"
        role="region"
        aria-label={`${track} effects`}
        data-track={track}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fx-dials-header">
          <span className="fx-dials-title">{track.toUpperCase()} FX</span>
          <button
            type="button"
            className="fx-dials-close"
            onClick={() => toggleFxPanel(track)}
            aria-label="Close effects"
          >
            ✕
          </button>
        </div>
        <div className="fx-dials-grid">
          {PARAMS.map((p) => (
            <Dial
              key={p.key}
              label={p.label}
              emoji={p.emoji}
              value={fx[p.key]}
              onChange={(v) => handleChange(p.key, v)}
            />
          ))}
        </div>
        <div
          className="fx-subdivisions"
          role="group"
          aria-label={`${track} feel`}
        >
          <span className="fx-subdivisions-label">FEEL</span>
          <button
            type="button"
            className="fx-subdivision-base"
            onClick={() => cycleSubdivisionBase(track)}
            aria-label={`Subdivision base, currently ${baseLabel(subdivisions.base)} note. Tap to cycle.`}
          >
            <span className="fx-subdivision-base-glyph" aria-hidden="true">
              {baseGlyph(subdivisions.base)}
            </span>
            <span className="fx-subdivision-base-text">
              {baseLabel(subdivisions.base)}
            </span>
          </button>
          <button
            type="button"
            className={`fx-subdivision-triplet ${subdivisions.triplet ? "is-active" : ""}`}
            onClick={() => toggleSubdivisionTriplet(track)}
            aria-pressed={subdivisions.triplet}
            aria-label="Triplet subdivision"
          >
            <span className="fx-subdivision-emoji" aria-hidden="true">
              3
            </span>
            <span className="fx-subdivision-text">TRIPLET</span>
          </button>
        </div>
        <p className="fx-subdivisions-hint" aria-live="polite">
          Grid: <strong>{baseDisplay}</strong>
          {subdivisions.triplet ? " · triplets" : ""}. Changing the feel clears
          this track's grid.
        </p>
      </aside>
    </div>
  );
}
